// src/pages/supplier/SamplesPage.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Package, Clock, CheckCircle, Truck, XCircle, Star, ChevronDown, ChevronUp, MapPin, AlertTriangle } from 'lucide-react'
import { supplierSamplesApi } from '../../api/services'
import { Card, CardHeader, CardBody, Button, StatusBadge, PageSpinner } from '../../components/common/ui'
import toast from 'react-hot-toast'

const STATUS_TABS = [
  { key: 'all',                label: 'All',        icon: Package },
  { key: 'pending',            label: 'Pending',    icon: Clock },
  { key: 'approved',           label: 'Approved',   icon: CheckCircle },
  { key: 'dispatched',         label: 'Dispatched', icon: Truck },
  { key: 'delivered',          label: 'Delivered',  icon: Package },
  { key: 'feedback_submitted', label: 'Reviewed',   icon: Star },
  { key: 'rejected',           label: 'Rejected',   icon: XCircle },
]

export default function SupplierSamplesPage() {
  const qc = useQueryClient()
  const [tab, setTab] = useState('all')
  const [expanded, setExpanded] = useState(null)
  const [dispatchForm, setDispatchForm] = useState({})
  const [rejectReason, setRejectReason] = useState({})
  const [approveNotes, setApproveNotes] = useState({})
  const [showReject, setShowReject] = useState(null)
  const [showApprove, setShowApprove] = useState(null)
  const [showDispatch, setShowDispatch] = useState(null)

  const { data, isLoading } = useQuery({
    queryKey: ['supplier-samples', tab],
    queryFn: () => supplierSamplesApi.list(tab !== 'all' ? { status: tab } : {}),
  })

  const approve = useMutation({
    mutationFn: ({ id, notes }) => supplierSamplesApi.approve(id, { notes }),
    onSuccess: () => { toast.success('Sample approved'); qc.invalidateQueries({ queryKey: ['supplier-samples'] }); setShowApprove(null) },
    onError: e => toast.error(e.response?.data?.message || 'Failed'),
  })

  const reject = useMutation({
    mutationFn: ({ id, reason }) => supplierSamplesApi.reject(id, { reason }),
    onSuccess: () => { toast.success('Sample rejected'); qc.invalidateQueries({ queryKey: ['supplier-samples'] }); setShowReject(null) },
    onError: e => toast.error(e.response?.data?.message || 'Failed'),
  })

  const dispatch = useMutation({
    mutationFn: ({ id, courier_name, tracking_number }) => supplierSamplesApi.dispatch(id, { courier_name, tracking_number }),
    onSuccess: () => { toast.success('Dispatched!'); qc.invalidateQueries({ queryKey: ['supplier-samples'] }); setShowDispatch(null); setDispatchForm({}) },
    onError: e => toast.error(e.response?.data?.message || 'Failed'),
  })

  if (isLoading) return <PageSpinner />

  const payload = data?.data?.data || {}
  const list = payload?.requests?.data || []
  const counts = payload?.counts || {}
  const totalCount = Object.values(counts).reduce((a, b) => a + b, 0)

  // Compute metrics
  const pendingCount = counts.pending || 0
  const approvedCount = counts.approved || 0
  const dispatchedCount = counts.dispatched || 0
  const feedbackCount = counts.feedback_submitted || 0

  return (
    <div>
      {/* Header */}
      <h1 className="text-xl font-display font-bold mb-6 flex items-center gap-2">
        <Package size={20} className="text-pink" /> Sample Requests
      </h1>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle size={16} className="text-amber-500" />
            <p className="text-xs text-muted font-bold uppercase tracking-wider">Action Required</p>
          </div>
          <p className="text-2xl font-extrabold text-gray-900">{pendingCount}</p>
          <p className="text-[11px] text-muted">Awaiting your response</p>
        </div>
        <div className="bg-white border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Truck size={16} className="text-blue-500" />
            <p className="text-xs text-muted font-bold uppercase tracking-wider">Ready to Ship</p>
          </div>
          <p className="text-2xl font-extrabold text-gray-900">{approvedCount}</p>
          <p className="text-[11px] text-muted">Approved, needs dispatch</p>
        </div>
        <div className="bg-white border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Package size={16} className="text-indigo-500" />
            <p className="text-xs text-muted font-bold uppercase tracking-wider">In Transit</p>
          </div>
          <p className="text-2xl font-extrabold text-gray-900">{dispatchedCount}</p>
          <p className="text-[11px] text-muted">Dispatched samples</p>
        </div>
        <div className="bg-white border border-border rounded-xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Star size={16} className="text-amber-500" />
            <p className="text-xs text-muted font-bold uppercase tracking-wider">Feedback</p>
          </div>
          <p className="text-2xl font-extrabold text-gray-900">{feedbackCount}</p>
          <p className="text-[11px] text-muted">Buyer feedback received</p>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="flex gap-1.5 mb-5 overflow-x-auto pb-1">
        {STATUS_TABS.map(t => {
          const count = t.key === 'all' ? totalCount : (counts[t.key] || 0)
          const Icon = t.icon
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all ${
                tab === t.key ? 'bg-pink text-white' : 'bg-surface text-muted hover:bg-gray-100'
              }`}
            >
              <Icon size={12} />
              {t.label}
              {count > 0 && <span className="text-[10px]">({count})</span>}
            </button>
          )
        })}
      </div>

      {/* Empty */}
      {list.length === 0 && (
        <div className="text-center py-16 text-muted text-sm">No sample requests in this category.</div>
      )}

      {/* Request Cards */}
      <div className="space-y-4">
        {list.map(sr => {
          const isExpanded = expanded === sr.id
          const isPending = sr.status === 'pending'
          const isApproved = sr.status === 'approved'
          const isRejected = sr.status === 'rejected'

          return (
            <Card key={sr.id} className={`${isPending ? 'border-amber-200' : ''} ${isRejected ? 'border-red-200 bg-red-50/30' : ''}`}>
              <CardBody>
                {/* Main Row */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-surface border border-border overflow-hidden flex-shrink-0">
                    {sr.product?.primary_image_url && (
                      <img src={sr.product.primary_image_url} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-bold text-sm text-gray-900 line-clamp-1">{sr.product?.name}</span>
                      <StatusBadge status={sr.status} />
                    </div>
                    <p className="text-xs text-muted">
                      From: <span className="font-bold">{sr.buyer?.name}</span> · {sr.request_number}
                    </p>
                    {sr.purpose && <p className="text-xs text-gray-600 mt-0.5">Purpose: {sr.purpose}</p>}
                    {sr.potential_order_metres && (
                      <p className="text-xs text-green-700 font-bold mt-0.5">Potential order: {sr.potential_order_metres}m</p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {/* Quick Actions */}
                    {isPending && (
                      <div className="flex gap-1.5">
                        <Button size="sm" onClick={() => { setShowApprove(sr.id); setExpanded(sr.id) }}>Approve</Button>
                        <Button size="sm" variant="danger" onClick={() => { setShowReject(sr.id); setExpanded(sr.id) }}>Reject</Button>
                      </div>
                    )}
                    {isApproved && (
                      <Button size="sm" variant="secondary" onClick={() => { setShowDispatch(sr.id); setExpanded(sr.id) }}>
                        <Truck size={14} /> Dispatch
                      </Button>
                    )}
                    <button onClick={() => setExpanded(isExpanded ? null : sr.id)} className="text-muted hover:text-gray-900">
                      {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </div>
                </div>

                {/* Expanded Detail */}
                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-border">
                    {/* Shipping Address */}
                    <div className="flex items-start gap-2 text-xs text-gray-600 mb-3">
                      <MapPin size={14} className="mt-0.5 text-muted flex-shrink-0" />
                      <div>
                        <p className="font-bold">{sr.shipping_name}</p>
                        <p>{sr.shipping_address}, {sr.shipping_city}, {sr.shipping_state} - {sr.shipping_pincode}</p>
                        <p>Phone: {sr.shipping_phone}</p>
                      </div>
                    </div>

                    {/* Tracking */}
                    {sr.tracking_number && (
                      <div className="flex items-center gap-2 text-xs text-indigo-700 bg-indigo-50 rounded-lg px-3 py-2 mb-3">
                        <Truck size={14} />
                        <span>{sr.courier_name} — <span className="font-mono font-bold">{sr.tracking_number}</span></span>
                      </div>
                    )}

                    {/* Rejection reason */}
                    {isRejected && sr.rejection_reason && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                        <p className="text-sm font-bold text-red-800">Rejected</p>
                        <p className="text-xs text-red-600 mt-0.5">{sr.rejection_reason}</p>
                      </div>
                    )}

                    {/* Buyer Feedback */}
                    {sr.feedback_rating && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                        <p className="text-xs font-bold text-amber-800 mb-1">Buyer Feedback</p>
                        <div className="flex items-center gap-1 mb-1">
                          {[1,2,3,4,5].map(i => (
                            <Star key={i} size={14} className={i <= sr.feedback_rating ? 'text-amber-500 fill-amber-500' : 'text-gray-300'} />
                          ))}
                          <span className="text-xs font-bold text-amber-700 ml-1">{sr.feedback_rating}/5</span>
                        </div>
                        {sr.feedback_notes && <p className="text-xs text-amber-700">{sr.feedback_notes}</p>}
                        {sr.will_place_bulk_order && (
                          <p className="text-xs text-green-700 font-bold mt-1">Buyer interested in bulk order!</p>
                        )}
                      </div>
                    )}

                    {/* Approve Form */}
                    {showApprove === sr.id && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                        <p className="text-sm font-bold text-green-800 mb-2">Approve Sample</p>
                        <textarea
                          className="input w-full h-16 mb-2"
                          placeholder="Optional notes for the buyer..."
                          value={approveNotes[sr.id] || ''}
                          onChange={e => setApproveNotes(n => ({ ...n, [sr.id]: e.target.value }))}
                        />
                        <div className="flex gap-2">
                          <Button size="sm" loading={approve.isPending} onClick={() => approve.mutate({ id: sr.id, notes: approveNotes[sr.id] })}>
                            Confirm Approve
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setShowApprove(null)}>Cancel</Button>
                        </div>
                      </div>
                    )}

                    {/* Reject Form */}
                    {showReject === sr.id && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                        <p className="text-sm font-bold text-red-800 mb-2">Reject Sample Request</p>
                        <textarea
                          className="input w-full h-16 mb-2"
                          placeholder="Reason for rejection..."
                          value={rejectReason[sr.id] || ''}
                          onChange={e => setRejectReason(r => ({ ...r, [sr.id]: e.target.value }))}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="danger"
                            loading={reject.isPending}
                            disabled={!(rejectReason[sr.id] || '').trim()}
                            onClick={() => reject.mutate({ id: sr.id, reason: rejectReason[sr.id] })}
                          >
                            Confirm Reject
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setShowReject(null)}>Cancel</Button>
                        </div>
                      </div>
                    )}

                    {/* Dispatch Form */}
                    {showDispatch === sr.id && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                        <p className="text-sm font-bold text-blue-800 mb-2">Dispatch Sample</p>
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <input
                            className="input"
                            placeholder="Courier name"
                            value={dispatchForm.courier_name || ''}
                            onChange={e => setDispatchForm(d => ({ ...d, courier_name: e.target.value }))}
                          />
                          <input
                            className="input"
                            placeholder="Tracking number"
                            value={dispatchForm.tracking_number || ''}
                            onChange={e => setDispatchForm(d => ({ ...d, tracking_number: e.target.value }))}
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            loading={dispatch.isPending}
                            disabled={!dispatchForm.courier_name?.trim() || !dispatchForm.tracking_number?.trim()}
                            onClick={() => dispatch.mutate({ id: sr.id, ...dispatchForm })}
                          >
                            Confirm Dispatch
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => { setShowDispatch(null); setDispatchForm({}) }}>Cancel</Button>
                        </div>
                      </div>
                    )}

                    {/* Timestamps */}
                    <div className="text-[10px] text-muted space-y-0.5 mt-2">
                      <p>Requested: {new Date(sr.created_at).toLocaleString('en-IN')}</p>
                      {sr.dispatched_at && <p>Dispatched: {new Date(sr.dispatched_at).toLocaleString('en-IN')}</p>}
                      {sr.delivered_at && <p>Delivered: {new Date(sr.delivered_at).toLocaleString('en-IN')}</p>}
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
